import React, { ReactNode, useEffect, useState } from 'react';
import CommandBar from './CommandBar';
import FilterBar from './FilterBar';
import ListPageSkeleton from './ListPageSkeleton';
import './list-page.scss';

export { default as CommandBar } from './CommandBar';
export { default as FilterBar } from './FilterBar';
export { default as FilterTrigger } from './FilterTrigger';
export { default as StatusSegments } from './StatusSegments';
export type { StatusSegment } from './StatusSegments';
export { default as SearchInput } from './SearchInput';

export interface ListPageProps {
  title: string;
  /** Command bar */
  search?: ReactNode;
  actions?: ReactNode;
  /** Filter bar */
  segments?: ReactNode;
  filters?: ReactNode;
  hasActiveFilters?: boolean;
  onClearFilters?: () => void;
  /** Static data (templates, team) not ready: the bars render as a skeleton. */
  loading?: boolean;
  /** Skeleton shape while loading. */
  skeleton?: { segmentCount?: number; filterCount?: number; showAction?: boolean };
  /** "Showing 1–25 of 312 documents", at the left of the footer. */
  resultsText?: ReactNode;
  /** A Pagination variant="bar", at the right of the footer. */
  footer?: ReactNode;
  className?: string;
  /** Inline CSS custom properties the body's own layout reads (the Budget grid). */
  style?: React.CSSProperties;
  /** The scrolling body: a Table with stickyHeader, a TableSkeleton, or an empty state. */
  children: ReactNode;
}

/** Whether a media query matches, kept current as the window resizes. */
export const useMediaQuery = (query: string): boolean => {
  const [matches, setMatches] = useState(() =>
    typeof window !== 'undefined' ? window.matchMedia(query).matches : false
  );
  useEffect(() => {
    const mq = window.matchMedia(query);
    setMatches(mq.matches);
    const onChange = (e: MediaQueryListEvent) => setMatches(e.matches);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, [query]);
  return matches;
};

/**
 * Whether the viewport is at or below the nav-collapse breakpoint. The
 * segments switch to `sm` there; the rest is CSS.
 */
export const useNarrowViewport = (): boolean => useMediaQuery('(max-width: 992px)');

/**
 * A laptop-width viewport, where a wide register drops a secondary column
 * rather than scroll sideways (the invoice register's Recorded date).
 */
export const useCompactTable = (): boolean => useMediaQuery('(max-width: 1365px)');

/**
 * The register shell: a pinned head (command bar + filter bar), a scrolling
 * body and a pinned 40px footer. Fills `.page-body` on an `internal-scroll`
 * route -- the route must be in App.tsx's COMMAND_BAR_PATHS, which also
 * suppresses the Layout's own h1. See DEVELOPMENT.md "List pages: the command
 * bar shell".
 */
const ListPage: React.FC<ListPageProps> = ({
  title,
  search,
  actions,
  segments,
  filters,
  hasActiveFilters = false,
  onClearFilters,
  loading = false,
  skeleton,
  resultsText,
  footer,
  className = '',
  style,
  children,
}) => {
  const showFilterBar = Boolean(segments) || Boolean(filters);
  return (
    <div className={`list-page ${className}`.trim()} style={style}>
      {/* overflow stays visible: dropdown menus are not portalled */}
      <div className="list-page__head">
        {loading ? (
          <ListPageSkeleton title={title} {...skeleton} />
        ) : (
          <>
            <CommandBar title={title} search={search} actions={actions} />
            {showFilterBar && (
              <FilterBar segments={segments} showClear={hasActiveFilters} onClear={onClearFilters}>
                {filters}
              </FilterBar>
            )}
          </>
        )}
      </div>
      <div className="list-page__scroll">{children}</div>
      {(footer || resultsText) && (
        <div className="list-page__footer">
          <div className="list-page__results">{loading ? null : resultsText}</div>
          <div className="list-page__pagination">{loading ? null : footer}</div>
        </div>
      )}
    </div>
  );
};

export default ListPage;
